// ============================================================
// TangoAlpha BT — shared plumbing for the backtest page.
//
// The pricing engine itself lives in src/backtest/ and is a verbatim copy of
// the sabr_dnn TypeScript engine — do not edit it here. It is pinned to the
// Python implementation by a golden-fixture parity test (`npm test`); editing
// one side without regenerating the fixtures turns that test red. See
// src/backtest/README.md.
//
// This file is the bridge: a worker hook, formatters, and chart/UI primitives
// in the TangoAlpha house style (theme.js tokens, Recharts, dark only).
// ============================================================
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ResponsiveContainer, ComposedChart, Line, Area, CartesianGrid,
  XAxis, YAxis, Tooltip, ReferenceLine,
} from 'recharts';
import { C, F, MONO } from './theme';
import { S, RADIUS } from './ui';
import { surfaceDataUrl } from '../backtest/dataUrl';

// ── Accent for this product tab (C.gn, distinct from FX/Rates/Curve) ────────
export const BT_ACCENT = '#34d399';

// Series colours, drawn from the existing palette so the BT tab reads as part
// of the suite rather than as an import.
export const SERIES = {
  pnl:     C.gn,
  theta:   C.ac,   // amber — the carry line
  gamma:   C.rd,   // red   — what you pay hedging
  vega:    C.cy,
  delta:   C.bl,
  annuity: C.pu,
  cost:    C.txM,
  resid:   C.bdL,
};

// ── Formatters ──────────────────────────────────────────────────────────────

// P&L at 100mm notional runs to 1e7; a raw "10,000,000" axis label overflows
// the gutter and reads worse than "10M".
export function compact(v) {
  if (v == null || !isFinite(v)) return '--';
  const a = Math.abs(v);
  if (a === 0) return '0';
  if (a >= 1e9) return (v / 1e9).toFixed(a >= 1e10 ? 0 : 1) + 'B';
  if (a >= 1e6) return (v / 1e6).toFixed(a >= 1e7 ? 0 : 1) + 'M';
  if (a >= 1e3) return (v / 1e3).toFixed(a >= 1e4 ? 0 : 1) + 'k';
  if (a >= 1)   return v.toFixed(a >= 100 ? 0 : 2);
  if (a >= 1e-3) return v.toFixed(4);
  return v.toExponential(1);
}

export function fmt(v, dp = 4) {
  if (v == null || !isFinite(v)) return '--';
  return v.toFixed(dp);
}

export function pct(v) {
  return v == null || !isFinite(v) ? '--' : (v * 100).toFixed(1) + '%';
}

export function isoFromDay(day) {
  return new Date(day * 86400000).toISOString().slice(0, 10);
}

export function monthLabel(iso) {
  if (typeof iso !== 'string') return '';
  const d = new Date(iso);
  const m = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][d.getUTCMonth()];
  return `${m} ${String(d.getUTCFullYear() % 100).padStart(2, '0')}`;
}

// ── Backtest worker hook ────────────────────────────────────────────────────
//
// The worker calls Backtest.run(). It does NOT re-implement the loop: the
// previous incarnation of this app chunked the run by hand for a progress bar
// and omitted the expiry-settlement step, so options never expired at all.
// `Backtest.stepOnce()` exists for chunking if it is ever needed.

export function useBacktest() {
  const workerRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const queue = useRef([]);
  const onDone = useRef(null);

  useEffect(() => {
    let w;
    try {
      w = new Worker(new URL('../backtest/worker.ts', import.meta.url), { type: 'module' });
    } catch (e) {
      setError('could not start the backtest worker: ' + e.message);
      return undefined;
    }
    w.onerror = (e) => { setBusy(false); setError('worker error: ' + (e.message || 'unknown')); };
    w.onmessage = (ev) => {
      if (ev.data.type === 'error') { setBusy(false); setError(ev.data.message); return; }
      setError(null);
      const cb = onDone.current;
      if (cb) { onDone.current = null; cb(ev.data); }
      else setResult(ev.data);
      if (queue.current.length) w.postMessage(queue.current.shift());
      // A completion callback may have chained a second run (the Compare tab
      // does exactly that). If it did, `onDone` is already set again and the
      // spinner must stay on; clearing it here would say "done" mid-sequence.
      else if (!onDone.current) setBusy(false);
    };
    workerRef.current = w;
    return () => w.terminate();
  }, []);

  const run = useCallback((cfg, done) => {
    const w = workerRef.current;
    if (!w) return;
    setBusy(true);
    setError(null);
    onDone.current = done || null;
    w.postMessage({
      type: 'run',
      dataUrl: surfaceDataUrl(),
      trades: cfg.trades,
      engine: cfg.engine ?? 'sabr',
      schedule: cfg.schedule ?? 'quarterly',
      roll: cfg.roll ?? true,
      hedge: cfg.hedge ?? '',
      start: cfg.start,
      end: cfg.end,
      volHalfSpreadBp: cfg.volHalfSpreadBp ?? 0.5,
      swapHalfSpreadBp: cfg.swapHalfSpreadBp ?? 0,
      fundingRate: cfg.fundingRate ?? 0,
      remarkEngine: cfg.remarkEngine,
    });
  }, []);

  return { run, busy, error, result, setResult };
}

// ── Chart ───────────────────────────────────────────────────────────────────
//
// One component for every time series on the page, matching RateChart's
// styling: dashed grid in C.bd, angled month ticks, no animation, no dots.
// 1,145 points x 6 series is the same order as the HISTORY chart on the Rates
// tab, so this size is already proven in the app.

export function BTChart({
  rows, series, height = 300, yFormat = compact, yWidth = 56,
  xKey = 'd', xNumeric = false, xFormat, zeroLine = true, domain,
}) {
  const tickEvery = Math.max(1, Math.floor((rows?.length || 1) / 12));
  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={rows} margin={{ top: 10, right: 12, bottom: 5, left: 0 }}>
        <defs>
          {series.filter(s => s.fill).map(s => (
            <linearGradient key={s.key} id={`btg_${s.key}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={s.color} stopOpacity={0.20} />
              <stop offset="95%" stopColor={s.color} stopOpacity={0} />
            </linearGradient>
          ))}
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke={C.bd} />
        <XAxis
          dataKey={xKey}
          type={xNumeric ? 'number' : 'category'}
          domain={xNumeric ? ['dataMin', 'dataMax'] : undefined}
          tick={{ fontSize: F.xs, fill: C.txD }}
          stroke={C.bd}
          interval={xNumeric ? undefined : tickEvery}
          tickFormatter={xNumeric ? xFormat : monthLabel}
          angle={xNumeric ? 0 : -30}
          textAnchor={xNumeric ? 'middle' : 'end'}
          height={xNumeric ? 26 : 42}
        />
        <YAxis
          tick={{ fontSize: F.sm, fill: C.txD }}
          stroke={C.bd}
          tickFormatter={yFormat}
          width={yWidth}
          domain={domain}
        />
        <Tooltip
          isAnimationActive={false}
          cursor={{ stroke: C.txM, strokeDasharray: '3 3', strokeOpacity: 0.45 }}
          contentStyle={S.tooltip}
          labelStyle={{ color: C.txD, fontSize: F.xs, marginBottom: 3 }}
          itemStyle={{ fontFamily: MONO, fontSize: F.sm, padding: 0 }}
          labelFormatter={(v) => (xNumeric ? (xFormat ? xFormat(v) : v) : v)}
          formatter={(v, name) => [yFormat(v), name]}
        />
        {zeroLine && <ReferenceLine y={0} stroke={C.bdL} strokeWidth={1} />}
        {series.map(s => (
          s.fill ? (
            <Area key={s.key} type="monotone" dataKey={s.key} name={s.label}
                  stroke={s.color} strokeWidth={s.width ?? 2}
                  fill={`url(#btg_${s.key})`} isAnimationActive={false}
                  dot={false} activeDot={{ r: 3 }} connectNulls={false} />
          ) : (
            <Line key={s.key} type="monotone" dataKey={s.key} name={s.label}
                  stroke={s.color} strokeWidth={s.width ?? 2}
                  strokeDasharray={s.dash} isAnimationActive={false}
                  dot={false} activeDot={{ r: 3 }} connectNulls={false} />
          )
        ))}
      </ComposedChart>
    </ResponsiveContainer>
  );
}

// ── UI atoms ────────────────────────────────────────────────────────────────

export function Tile({ k, v, s, color }) {
  return (
    <div style={{ background: C.bgC, border: `1px solid ${C.bd}`,
                  borderRadius: RADIUS.md, padding: '8px 11px', minWidth: 0 }}>
      <div style={{ ...S.label, fontSize: F.xxs }}>{k}</div>
      <div style={{ fontFamily: MONO, fontSize: F.d2, fontWeight: 600,
                    color: color || C.tx, lineHeight: 1.15, marginTop: 1 }}>{v}</div>
      {s ? <div style={{ fontSize: F.xxs, color: C.txM, marginTop: 1 }}>{s}</div> : null}
    </div>
  );
}

export function Tiles({ children }) {
  return (
    <div style={{ display: 'grid', gap: 8,
                  gridTemplateColumns: 'repeat(auto-fit, minmax(132px, 1fr))' }}>
      {children}
    </div>
  );
}

// Every chart carries its series values in the legend, not only in the hover
// tooltip — identity is never colour-alone.
export function Legend({ items }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px',
                  padding: '8px 2px 2px', fontSize: F.sm }}>
      {items.map(i => (
        <span key={i.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: C.txD }}>
          <span style={{ width: 11, height: 3, borderRadius: 2, background: i.color, flex: 'none' }} />
          {i.label}
          {i.value != null && (
            <span style={{ fontFamily: MONO, color: C.tx, fontWeight: 600 }}>{i.value}</span>
          )}
        </span>
      ))}
    </div>
  );
}

export function Panel({ title, subtitle, children, note }) {
  return (
    <div style={{ borderBottom: `1px solid ${C.bd}`, padding: `12px 14px 14px` }}>
      {title && (
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: subtitle ? 2 : 8 }}>
          <div style={{ fontSize: F.h3, fontWeight: 700, letterSpacing: '-.01em' }}>{title}</div>
        </div>
      )}
      {subtitle && (
        <div style={{ fontSize: F.sm, color: C.txD, marginBottom: 8, maxWidth: '86ch' }}>{subtitle}</div>
      )}
      {children}
      {note && (
        <div style={{ fontSize: F.xs, color: C.txM, marginTop: 8, maxWidth: '96ch', lineHeight: 1.5 }}>{note}</div>
      )}
    </div>
  );
}

// The verdict that says whether the rest of the page is worth reading.
export function InvariantBadge({ inv }) {
  if (!inv) return null;
  const ok = inv.ok;
  const col = ok ? C.gn : C.rd;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                  padding: '8px 14px', borderBottom: `1px solid ${C.bd}`,
                  background: ok ? C.gnD : C.rdD }}>
      <span style={{ padding: '2px 9px', borderRadius: RADIUS.md, fontSize: F.sm,
                     fontWeight: 700, background: `${col}20`, color: col,
                     border: `1px solid ${col}40`, whiteSpace: 'nowrap' }}>
        {ok ? '● INVARIANTS PASS' : '● INVARIANTS FAILED'}
      </span>
      <span style={{ fontSize: F.sm, color: C.txD }}>
        {ok
          ? <>NAV = cash + marks, cash matches the trade log, and the largest unexplained
              daily P&amp;L is <span style={{ fontFamily: MONO, color: C.tx }}>
              {isFinite(inv.maxUnexplained) ? inv.maxUnexplained.toExponential(2) : '--'}</span>.</>
          : inv.message}
      </span>
    </div>
  );
}

export function Field({ label, children, hint }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ ...S.label, marginBottom: 3 }}>{label}</div>
      {children}
      {hint && <div style={{ fontSize: F.xxs, color: C.txM, marginTop: 3, lineHeight: 1.45 }}>{hint}</div>}
    </div>
  );
}

export const fullInput = { ...S.input, width: '100%', boxSizing: 'border-box' };
export const fullSelect = { ...S.select, width: '100%', boxSizing: 'border-box' };

export function runButton(busy) {
  return {
    width: '100%', padding: '7px 10px', marginTop: 4,
    background: busy ? C.bgH : `${BT_ACCENT}20`,
    border: `1px solid ${busy ? C.bd : BT_ACCENT}`,
    borderRadius: RADIUS.sm, color: busy ? C.txM : BT_ACCENT,
    fontFamily: MONO, fontSize: F.lg, fontWeight: 700,
    cursor: busy ? 'default' : 'pointer',
  };
}

// ── Row builders (engine output -> Recharts rows) ───────────────────────────

export function pnlRows(res) {
  return res.days.map((d, i) => ({
    d: isoFromDay(d),
    pnl: res.nav[i],
    gross: res.navBeforeCosts[i],
  }));
}

export function attributionRows(res) {
  const a = res.attribution;
  return res.days.map((d, i) => ({
    d: isoFromDay(d),
    theta: a.theta[i], gamma: a.gamma[i], vega: a.vega[i],
    delta: a.delta[i], annuity: a.annuity[i], cost: a.cost[i],
  }));
}

export function seriesRows(res, key) {
  return res.days.map((d, i) => ({ d: isoFromDay(d), v: res[key][i] }));
}
